import superagent from 'superagent'
import formatUrl from './formatUrl'
import { isFunction } from 'lodash'

const methods = ['get', 'post', 'put', 'patch', 'delete']

const localStorageKey = 'fiton-admin:local:token'
const localStorageUserKey = 'fiton-admin:local:admin-id'
const localStorageUserNameKey = 'fiton-admin:local:admin-name'

export function getToken() {
  return localStorage.getItem(localStorageKey)
}

export function setToken(token) {
  return localStorage.setItem(localStorageKey, token)
}

export function getAdminId() {
  return localStorage.getItem(localStorageUserKey)
}

export function setAdminId(id) {
  return localStorage.setItem(localStorageUserKey, id)
}

export function setAdminName(name) {
  return localStorage.setItem(localStorageUserNameKey, name)
}

export function getAdminName() {
  return localStorage.getItem(localStorageUserNameKey)
}

export function clearUserInfo() {
  setToken('')
  setAdminId('')
  setAdminName('')
}

/**
 * FEA-309. The whole 401 decision, as one testable unit: does this response mean "your session is
 * over, go to the sign-in screen", and if so where?
 *
 * Extracted alongside the predicate below because the predicate alone left the part that actually
 * runs -- the `body.code === 401` check, clearing the stored session, and the redirect target --
 * with no coverage at all: deleting the path check from the request callback kept every test green.
 */
export function unauthorizedRedirect(path, body, status) {
  // Either source counts. Keying off the envelope alone missed a 401 produced in FRONT of the API --
  // a load balancer, a proxy, Koa's own default -- which carries no `{ status, msg, code, data }`
  // body at all. That is a dead session just as much as an API 401 is, and it is precisely the case
  // the 24-hour expiry makes routine, so treating it as "not a 401" stranded the admin on a dead
  // console screen with no way back.
  const isUnauthorized = (body && body.code === 401) || status === 401
  if (!isUnauthorized) return null
  if (!shouldRedirectOnUnauthorized(path)) return null
  return '/login'
}

/**
 * Does a 401 on this path mean "your session is over, go to the sign-in screen"?
 *
 * Before admin tokens expired, this only ever fired for `/login`, so every other path's 401 surfaced
 * as a generic error toast with no route back to sign-in. That cost nothing while admin tokens were
 * immortal. With a 24-hour expiry it costs every admin, once a day, on whatever screen they happen
 * to be on -- so the redirect has to cover every path, not just the login one.
 *
 * The two MFA steps are the exceptions. Their 401s are the flow working as designed -- a wrong or
 * expired passcode -- and the sign-in screen renders them inline. Bouncing the browser would throw
 * away the challenge the admin is part-way through and read as "the system broke".
 *
 * `/login` itself deliberately still redirects, even though a 401 there means "wrong password" and
 * not "dead session". FEA-309's acceptance is explicit that a wrong password must behave exactly as
 * it does today, and today it clears the stored session and reloads the sign-in screen. The cost is
 * that the inline "Incorrect email or password" copy is pre-empted by the reload and rarely renders
 * -- worth fixing, but it is a change to behaviour the ticket froze, so it belongs to whoever owns
 * that decision rather than to this PR. Note the 502 case (a failed passcode send) is NOT affected:
 * it is not a 401, so no redirect fires and it renders inline as intended.
 *
 * Exported so it can be tested directly; the request callback below is not reachable from a test
 * without a live server.
 */
export function shouldRedirectOnUnauthorized(path) {
  const cleanPath = String(path || '').split('?')[0].replace(/\/+$/, '')
  return !/\/login\/(verify|resend)$/.test(cleanPath)
}

export default class ApiClient {
  constructor () {
    methods.forEach(method => {
      this[method] = (path, { params, body, header } = {}) => {
        this.toggleProcess(this.needProcess && true)

        const request = superagent[method](formatUrl(path))
        if (params) {
          request.query(params)
        }

        if (header) {
          for (let i in header) {
            request.set(i, header[i])
          }
        }

        request.set('Authorization', `Bearer ${getToken()}`)

        if (body) {
          request.send(body)
        }

        return new Promise((resolve, reject) => {
          request.end((err, { body, text } = {}) => {
            let msgType = 'success'
            if (err) {
              msgType = 'error'
              // superagent sets `message` to the HTTP status text ("Bad Request"), so a caller
              // rendering err.message inline shows the one thing that is never the reason. The
              // server's own message is in body.msg -- the same string the toast uses.
              if (body && body.msg) err.message = body.msg
              reject(err)
              // FEA-309: was `path.indexOf('/login') !== -1`, which redirected on the ONE path where
              // a 401 is not a dead session and on none of the paths where it is.
              const redirectTo = unauthorizedRedirect(path, body, err && err.status)
              if (redirectTo) {
                clearUserInfo()
                this.toggleProcess(false)
                return (window.location.href = redirectTo)
              }
            }
            resolve(body || text || err)

            if (body && body.msg && this.handleNotification) {
              this.handleNotification(msgType, body.msg)
            }
            this.toggleProcess(false)
          })
        })
      }
    })
  }
  
  toggleProcess (open) {
    isFunction(this.toggleProgress) && this.toggleProgress(open)
  }

  empty() {}
}

let _apiClient
export const getApiClient = (props = {}) => {
  const { needProcess = true } = props

  if (!_apiClient) {
    _apiClient = new ApiClient()
  }

  _apiClient.needProcess = needProcess

  return _apiClient
}
